import React, { useCallback, useState } from "react";

/*
 * Minimal EU ad-consent gate for AdSense, without a full IAB TCF CMP.
 * Google allows this simpler route for publishers who only serve their own
 * AdSense ads (no other ad-tech vendors): default to non-personalized
 * (contextual) ads for everyone, and let the banner upgrade a visitor to
 * personalized ads if they opt in.
 * https://developers.google.com/adsense/platforms/direct/gdpr
 *
 * We can't force anyone to answer the banner, so "hasn't answered" defaults
 * to non-personalized rather than showing no ads at all — same outcome as
 * explicitly clicking "basic", just without making that the visitor's saved
 * choice (the banner keeps showing until they do decide).
 */

const STORAGE_KEY = "mapli_ad_consent"; // "personalized" | "basic"

function readStoredConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private mode / storage blocked — consent just won't persist across visits.
  }
}

// Must be set before adsbygoogle.push({}) is ever called for this visitor.
function applyConsentFlag(choice) {
  window.adsbygoogle = window.adsbygoogle || [];
  window.adsbygoogle.requestNonPersonalizedAds = choice === "personalized" ? 0 : 1;
}

// Runs once, as soon as this module loads — well before any component
// mounts or any <AdSlot> can push an ad — so the very first ad request of
// the session already carries the right flag with no ordering risk.
applyConsentFlag(readStoredConsent());

export function useAdConsent() {
  const [consent, setConsent] = useState(readStoredConsent);

  const choose = useCallback((choice) => {
    writeStoredConsent(choice);
    applyConsentFlag(choice);
    setConsent(choice);
  }, []);

  return { consent, choose };
}

const COPY = {
  no: {
    text: "Mapli viser ikke-personlige annonser for å holde tjenesten gratis. Tillater du personlig tilpassede annonser i stedet?",
    accept: "Godta personlige annonser",
    basic: "Nei, behold ikke-personlige",
    privacy: "Personvern",
  },
  en: {
    text: "Mapli shows non-personalized ads to keep the service free. Allow personalized ads instead?",
    accept: "Allow personalized ads",
    basic: "No, keep non-personalized",
    privacy: "Privacy",
  },
};

export function AdConsentBanner({ consent, onChoose, lang = "no" }) {
  if (consent) return null;
  const t = COPY[lang] || COPY.no;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        background: "var(--ink-900)",
        color: "#fff",
        padding: "14px 20px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
      }}>
      <span style={{ font: "var(--type-body)", maxWidth: 480 }}>
        {t.text}{" "}
        <a href="/privacy" style={{ color: "#fff", textDecoration: "underline" }}>
          {t.privacy}
        </a>
      </span>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={() => onChoose("basic")}
          style={{ border: "1px solid #fff", background: "transparent", color: "#fff", borderRadius: "var(--radius-pill)", padding: "8px 16px", cursor: "pointer", font: "var(--type-body)" }}>
          {t.basic}
        </button>
        <button
          onClick={() => onChoose("personalized")}
          style={{ border: "none", background: "var(--mapli-coral)", color: "#fff", borderRadius: "var(--radius-pill)", padding: "8px 16px", cursor: "pointer", font: "var(--type-body-strong)" }}>
          {t.accept}
        </button>
      </div>
    </div>
  );
}
